<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

import {
  mcpServerTemplates,
  categories,
  transportOptions,
  getTransportBadge,
  getCategoryColor,
} from './data/mcpTemplates';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const selectedCategory = ref('all');
const searchQuery = ref('');

// Agent's configured MCP servers
const mcpServers = ref([]);

// Dialog refs
const catalogDialogRef = ref(null);
const configureDialogRef = ref(null);
const customDialogRef = ref(null);

const editingServer = ref(null);
const configuringTemplate = ref(null);

// Template configuration form (for adding from catalog)
const templateEnvForm = reactive({});

// Custom MCP Server Form
const customServerForm = reactive({
  name: '',
  transport: 'stdio',
  command: '',
  args: '',
  env: '{}',
  url: '',
  enabled: true,
});

// Computed filtered templates
const filteredTemplates = computed(() => {
  let templates = mcpServerTemplates;

  if (selectedCategory.value !== 'all') {
    templates = templates.filter(t => t.category === selectedCategory.value);
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    templates = templates.filter(
      t =>
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
    );
  }

  return templates;
});

const updateAgentFromData = agentData => {
  if (!agentData) return;
  mcpServers.value = agentData.mcp_servers || [];
};

onMounted(async () => {
  if (agent.value) {
    updateAgentFromData(agent.value);
  }
});

watch(
  agent,
  newAgent => {
    if (newAgent) updateAgentFromData(newAgent);
  },
  { deep: false }
);

const isServerConfigured = serverId => {
  return mcpServers.value.some(
    s => s.id === serverId || s.templateId === serverId
  );
};

const getTemplateById = templateId => {
  return mcpServerTemplates.find(t => t.id === templateId);
};

// Open configure modal for template
const openConfigureModal = template => {
  configuringTemplate.value = template;
  template.requiredEnv.forEach(env => {
    templateEnvForm[env.key] = '';
  });
  configureDialogRef.value.dialogRef.open();
};

// Add server from template with configured credentials
const addServerFromTemplate = async () => {
  if (!configuringTemplate.value) return;
  if (!props.agentId) return;

  const template = configuringTemplate.value;

  const missingFields = template.requiredEnv.filter(
    env => !templateEnvForm[env.key] && !env.key.includes('opcional')
  );

  if (missingFields.length > 0) {
    useAlert(
      `Preencha os campos obrigatorios: ${missingFields.map(f => f.label).join(', ')}`
    );
    return;
  }

  try {
    const env = {};
    template.requiredEnv.forEach(envConfig => {
      if (templateEnvForm[envConfig.key]) {
        env[envConfig.key] = templateEnvForm[envConfig.key];
      }
    });

    const newServer = {
      id: `${template.id}-${Date.now()}`,
      templateId: template.id,
      name: template.name,
      description: template.description,
      transport: template.transport,
      command: template.command,
      args: [...template.args],
      env,
      enabled: true,
    };

    const newServers = [...mcpServers.value, newServer];

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    configureDialogRef.value.dialogRef.close();
    configuringTemplate.value = null;
    Object.keys(templateEnvForm).forEach(key => {
      templateEnvForm[key] = '';
    });
    useAlert(`${template.name} adicionado com sucesso!`);
  } catch (error) {
    useAlert(error?.message || 'Erro ao adicionar servidor MCP');
  }
};

const toggleServer = async server => {
  if (!props.agentId) return;

  try {
    const updatedServers = mcpServers.value.map(s => {
      if (s.id === server.id) {
        return { ...s, enabled: !s.enabled };
      }
      return s;
    });

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      mcp_servers: updatedServers,
    });

    mcpServers.value = updatedServers;
    useAlert(`Servidor ${server.enabled ? 'desativado' : 'ativado'}`);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar servidor');
  }
};

const removeServer = async server => {
  if (!props.agentId) return;

  try {
    const newServers = mcpServers.value.filter(s => s.id !== server.id);

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    useAlert('Servidor removido');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover servidor');
  }
};

const openCustomModal = (server = null) => {
  if (server) {
    editingServer.value = server;
    customServerForm.name = server.name;
    customServerForm.transport = server.transport || 'stdio';
    customServerForm.command = server.command || '';
    customServerForm.args = Array.isArray(server.args)
      ? server.args.join(' ')
      : server.args || '';
    customServerForm.env = JSON.stringify(server.env || {}, null, 2);
    customServerForm.url = server.url || '';
    customServerForm.enabled = server.enabled !== false;
  } else {
    editingServer.value = null;
    customServerForm.name = '';
    customServerForm.transport = 'stdio';
    customServerForm.command = '';
    customServerForm.args = '';
    customServerForm.env = '{}';
    customServerForm.url = '';
    customServerForm.enabled = true;
  }
  customDialogRef.value.dialogRef.open();
};

const saveCustomServer = async () => {
  if (!props.agentId) return;

  try {
    let env = {};
    try {
      env = JSON.parse(customServerForm.env || '{}');
    } catch {
      useAlert('Variaveis de ambiente deve ser um JSON valido');
      return;
    }

    const serverData = {
      id: editingServer.value?.id || `custom-${Date.now()}`,
      name: customServerForm.name,
      transport: customServerForm.transport,
      command: customServerForm.command,
      args: customServerForm.args.split(' ').filter(a => a),
      env,
      url: customServerForm.url,
      enabled: customServerForm.enabled,
      custom: true,
    };

    let newServers;

    if (editingServer.value) {
      newServers = mcpServers.value.map(s => {
        if (s.id === editingServer.value.id) {
          return serverData;
        }
        return s;
      });
    } else {
      newServers = [...mcpServers.value, serverData];
    }

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    customDialogRef.value.dialogRef.close();
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('MCP salvo e sincronizado');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar servidor MCP');
  }
};

const handleTransportChange = value => {
  customServerForm.transport = value;
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <SettingsHeader
      heading="Servidores MCP"
      description="Configure servidores MCP para estender as capacidades do agente."
    />

    <!-- Action Buttons -->
    <div class="flex items-center justify-end gap-2 mt-4 mb-6">
      <Button
        label="Servidor Personalizado"
        icon="i-lucide-plus"
        variant="secondary"
        size="sm"
        @click="openCustomModal()"
      />
      <Button
        label="Catalogo MCP"
        icon="i-lucide-library"
        size="sm"
        @click="catalogDialogRef.dialogRef.open()"
      />
    </div>

    <!-- Configured MCP Servers -->
    <div class="mb-8">
      <h4
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-server text-n-slate-11" />
        Servidores Configurados
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          {{ mcpServers.length }}
        </span>
      </h4>

      <div
        v-if="mcpServers.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
      >
        <div
          class="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center"
        >
          <span class="i-lucide-plug text-3xl text-purple-500" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhum servidor MCP configurado
        </h4>
        <p class="text-sm text-n-slate-11 mb-4 max-w-md">
          Adicione servidores MCP do catalogo para conectar seu agente a
          servicos externos como Google Calendar, GitHub, Slack e muito mais.
        </p>
        <Button
          label="Explorar Catalogo"
          icon="i-lucide-library"
          @click="catalogDialogRef.dialogRef.open()"
        />
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="server in mcpServers"
          :key="server.id"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl transition-all"
          :class="{ 'opacity-60': !server.enabled }"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-xl flex items-center justify-center"
              :class="
                server.enabled
                  ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20'
                  : 'bg-n-alpha-2'
              "
            >
              <span
                class="text-2xl"
                :class="[
                  getTemplateById(server.templateId)?.icon || 'i-lucide-plug',
                  server.enabled
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-n-slate-10',
                ]"
              />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <p class="font-medium text-n-slate-12">{{ server.name }}</p>
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="getTransportBadge(server.transport).class"
                >
                  {{ getTransportBadge(server.transport).label }}
                </span>
                <span
                  v-if="server.custom"
                  class="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  Custom
                </span>
              </div>
              <p class="text-xs text-n-slate-10 mt-0.5">
                {{
                  server.description ||
                  `${server.command} ${server.args?.join(' ') || ''}`
                }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Switch
              :model-value="server.enabled"
              @change="toggleServer(server)"
            />
            <Button
              icon="i-lucide-settings"
              variant="ghost"
              size="sm"
              @click="openCustomModal(server)"
            />
            <ConfirmButton
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              color="ruby"
              label=""
              confirm-label=""
              confirm-hint="Confirmar?"
              @click="removeServer(server)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Info Card -->
    <div
      class="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-xl"
    >
      <div class="flex gap-3">
        <span
          class="i-lucide-info text-purple-600 dark:text-purple-400 text-xl flex-shrink-0"
        />
        <div>
          <h4 class="font-medium text-purple-900 dark:text-purple-100 mb-1">
            O que e MCP?
          </h4>
          <p class="text-sm text-purple-700 dark:text-purple-300">
            O Model Context Protocol (MCP) e um padrao aberto que permite
            conectar agentes de IA a diferentes fontes de dados e ferramentas.
            Com MCP, seu agente pode acessar Google Calendar, GitHub, bancos de
            dados, APIs e muito mais de forma segura.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- MCP Catalog Dialog -->
  <Dialog
    ref="catalogDialogRef"
    title="Catalogo de Servidores MCP"
    description="Selecione um servidor para adicionar ao seu agente"
    :show-confirm-button="false"
    :overflow-y-auto="false"
    width="lg"
  >
    <!-- Search -->
    <div class="mb-4">
      <Input
        v-model="searchQuery"
        placeholder="Buscar servidores..."
        icon="i-lucide-search"
      />
    </div>

    <!-- Categories -->
    <div class="flex gap-2 overflow-x-auto pb-3 mb-4">
      <button
        v-for="cat in categories"
        :key="cat.id"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="
          selectedCategory === cat.id
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="selectedCategory = cat.id"
      >
        <span :class="cat.icon" class="text-sm" />
        {{ cat.name }}
      </button>
    </div>

    <!-- Template Grid -->
    <div class="overflow-y-auto max-h-[50vh]">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          v-for="template in filteredTemplates"
          :key="template.id"
          class="flex items-start gap-3 p-4 rounded-xl border text-left w-full transition-all group"
          :class="
            isServerConfigured(template.id)
              ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 cursor-not-allowed'
              : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1 hover:shadow-md'
          "
          :disabled="isServerConfigured(template.id)"
          @click="
            !isServerConfigured(template.id) && openConfigureModal(template)
          "
        >
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
            :class="
              isServerConfigured(template.id)
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-n-alpha-2'
            "
          >
            <span
              class="text-2xl"
              :class="[
                template.icon,
                isServerConfigured(template.id)
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-n-slate-11',
              ]"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="font-medium text-n-slate-12">{{ template.name }}</p>
              <span
                v-if="isServerConfigured(template.id)"
                class="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              >
                Configurado
              </span>
              <span
                v-if="template.requiredEnv.length === 0"
                class="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                Sem config
              </span>
            </div>
            <p class="text-sm text-n-slate-10 mt-1 line-clamp-2">
              {{ template.description }}
            </p>
            <div class="flex items-center gap-2 mt-2">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded"
                :class="getCategoryColor(template.category)"
              >
                {{
                  categories.find(c => c.id === template.category)?.name ||
                  template.category
                }}
              </span>
              <span
                v-if="template.requiredEnv.length > 0"
                class="text-xs text-n-slate-10"
              >
                {{ template.requiredEnv.length }} credencial(is)
              </span>
            </div>
          </div>
          <span
            v-if="!isServerConfigured(template.id)"
            class="i-lucide-chevron-right text-n-slate-10 group-hover:text-n-brand transition-colors"
          />
          <span
            v-else
            class="i-lucide-check text-green-600 dark:text-green-400"
          />
        </button>
      </div>

      <div v-if="filteredTemplates.length === 0" class="text-center py-12">
        <span class="i-lucide-search-x text-4xl text-n-slate-10 mb-3" />
        <p class="text-n-slate-11">Nenhum servidor encontrado</p>
      </div>
    </div>
  </Dialog>

  <!-- Configure Template Dialog -->
  <Dialog
    ref="configureDialogRef"
    :title="'Configurar ' + (configuringTemplate?.name || '')"
    description="Preencha as credenciais necessarias"
    confirm-button-label="Adicionar Servidor"
    :is-loading="isUpdating"
    overflow-y-auto
    width="lg"
    @confirm="addServerFromTemplate"
  >
    <template v-if="configuringTemplate">
      <!-- Description -->
      <div class="p-3 bg-n-alpha-1 rounded-lg mb-4">
        <p class="text-sm text-n-slate-11">
          {{ configuringTemplate.description }}
        </p>
      </div>

      <!-- Required Environment Variables -->
      <div v-if="configuringTemplate.requiredEnv.length > 0" class="space-y-4">
        <Input
          v-for="envConfig in configuringTemplate.requiredEnv"
          :key="envConfig.key"
          v-model="templateEnvForm[envConfig.key]"
          :label="
            envConfig.label + (!envConfig.key.includes('opcional') ? ' *' : '')
          "
          :type="envConfig.type || 'text'"
          :placeholder="envConfig.placeholder"
        />
      </div>

      <div
        v-else
        class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
      >
        <div class="flex items-center gap-2">
          <span
            class="i-lucide-check-circle text-green-600 dark:text-green-400"
          />
          <p class="text-sm text-green-700 dark:text-green-300">
            Este servidor nao requer configuracao adicional!
          </p>
        </div>
      </div>

      <!-- Documentation Link -->
      <a
        v-if="configuringTemplate.documentation"
        :href="configuringTemplate.documentation"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-2 text-sm text-n-brand hover:underline mt-4"
      >
        <span class="i-lucide-external-link" />
        Como obter as credenciais
      </a>
    </template>
  </Dialog>

  <!-- Custom Server Dialog -->
  <Dialog
    ref="customDialogRef"
    :title="
      (editingServer ? 'Editar' : 'Adicionar') + ' Servidor MCP Personalizado'
    "
    description="Configure um servidor MCP personalizado manualmente."
    confirm-button-label="Salvar"
    :is-loading="isUpdating"
    overflow-y-auto
    width="lg"
    @confirm="saveCustomServer"
  >
    <div class="space-y-4">
      <Input
        v-model="customServerForm.name"
        label="Nome do Servidor"
        placeholder="My Custom Server"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Tipo de Transporte
        </label>
        <ComboBox
          :model-value="customServerForm.transport"
          :options="transportOptions"
          placeholder="Selecione o transporte..."
          @update:model-value="handleTransportChange"
        />
      </div>

      <template v-if="customServerForm.transport === 'stdio'">
        <Input
          v-model="customServerForm.command"
          label="Comando"
          placeholder="npx"
        />

        <Input
          v-model="customServerForm.args"
          label="Argumentos (separados por espaco)"
          placeholder="-y @modelcontextprotocol/server-filesystem /path"
        />
      </template>

      <template v-else>
        <Input
          v-model="customServerForm.url"
          label="URL do Servidor"
          placeholder="http://localhost:3001/sse"
        />
      </template>

      <TextArea
        v-model="customServerForm.env"
        label="Variaveis de Ambiente (JSON)"
        auto-height
        :min-height="100"
        placeholder='{"API_KEY": "your-key", "SECRET": "your-secret"}'
      />

      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          Servidor habilitado
        </span>
        <Switch v-model="customServerForm.enabled" />
      </div>
    </div>
  </Dialog>
</template>
